'use client';

import { useCallback } from 'react';
import {
  usePaywallPayload,
  useTokenBalance,
} from '@/features/subscription/model/subscription.selectors';
import { useRenderBlockReason } from '@/features/design/job-queue';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { useFormData } from '@/features/design/render/model/render-form.store';
import {
  trackEvent,
  AnalyticsEvent,
  BlockReason,
} from '@/shared/lib/analytics';

/**
 * Render Generate-button preflight.
 *
 * The server is the single source of truth for whether a render may proceed.
 * The queue aggregate (REST `GET /v1/queue/status` + WS `queue:snapshot`)
 * carries `block_reason`, computed once server-side with priority already
 * applied (tokens > queue). This hook does NOT recompute renderability — no
 * token-balance math, no subscription-state checks, no client slot counting.
 * It only maps the server `blockReason` to the matching paywall:
 *
 *   - `subscription_required` → SUBSCRIPTION_REQUIRED (dormant; free tier
 *     renders post-pivot, so the server won't emit this now)
 *   - `insufficient_tokens`   → INSUFFICIENT_TOKENS
 *   - `queue_full`            → RENDER_QUEUE_LIMIT_EXCEEDED
 *   - `null`                  → proceed
 *
 * Returns `true` when a paywall was shown — caller MUST abort the submit.
 * Returns `false` when no gate matched — caller proceeds with submit.
 *
 * Why this exists: every render entry point (the stacked floating capsule and
 * the two-column inspector footer CTA) must run the same gate; centralizing it
 * here keeps cross-platform parity and prevents any path from silently dropping
 * clicks when a gate would have fired.
 */
export function useRenderPreflight(): () => boolean {
  const blockReason = useRenderBlockReason();
  const subscriptionPaywall = usePaywallPayload('SUBSCRIPTION_REQUIRED');
  const insufficientTokensPaywall = usePaywallPayload('INSUFFICIENT_TOKENS');
  const renderQueuePaywall = usePaywallPayload('RENDER_QUEUE_LIMIT_EXCEEDED');
  const showPaywall = useLimitPaywallStore((s) => s.showPaywall);
  const mode = useFormData().mode;
  const tokensBalance = useTokenBalance();

  return useCallback((): boolean => {
    // Record intent before any gate — this is the only signal the server never
    // sees when a preflight paywall aborts the submit below. (angle_kind is
    // carried on render_created/render_error where the angle id is known.)
    trackEvent(AnalyticsEvent.GENERATE_TAPPED, {
      mode,
      tokens_balance: tokensBalance,
    });
    if (blockReason === 'subscription_required' && subscriptionPaywall) {
      trackEvent(AnalyticsEvent.GENERATE_BLOCKED, {
        reason: BlockReason.SUBSCRIPTION_REQUIRED,
        mode,
        tokens_balance: tokensBalance,
      });
      showPaywall(subscriptionPaywall, 'SUBSCRIPTION_REQUIRED');
      return true;
    }
    if (blockReason === 'insufficient_tokens' && insufficientTokensPaywall) {
      trackEvent(AnalyticsEvent.GENERATE_BLOCKED, {
        reason: BlockReason.INSUFFICIENT_TOKENS,
        mode,
        tokens_balance: tokensBalance,
      });
      showPaywall(insufficientTokensPaywall, 'INSUFFICIENT_TOKENS');
      return true;
    }
    if (blockReason === 'queue_full' && renderQueuePaywall) {
      trackEvent(AnalyticsEvent.GENERATE_BLOCKED, {
        reason: BlockReason.QUEUE_FULL,
        mode,
        tokens_balance: tokensBalance,
      });
      showPaywall(renderQueuePaywall, 'RENDER_QUEUE_LIMIT_EXCEEDED');
      return true;
    }
    return false;
  }, [
    blockReason,
    subscriptionPaywall,
    insufficientTokensPaywall,
    renderQueuePaywall,
    showPaywall,
    mode,
    tokensBalance,
  ]);
}
